/**
 * 商談ボード (カンバン)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.2
 *
 * - 8列構成: document_requested → briefing → quote → won → paid → ordered → delivered → lost
 * - @dnd-kit でドラッグ&ドロップ。DnD で stage 変更
 * - won/lost ドロップは WonLostDialog で確定 (金額・失注理由が必須)
 * - フィルタ: 自分の担当 (ON/OFF) / 担当者 / 商品ライン / kind (BtoB/BtoC)。
 *   条件は localStorage に保存しリロード後もキープ
 */
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { DealStage } from '@goenhub/shared';
import { inspectionProgress } from '@goenhub/shared';
import {
  Building2,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  Filter,
  Inbox,
  Package,
  UserCheck,
  UserCircle2,
  Tag,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DealsInboxContent } from './inbox/DealsInboxPage';
import { WonLostDialog } from './WonLostDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useBriefingRecords } from '@/hooks/useBriefings';
import { useDeals, useTransitionDealStage, type DealRecord } from '@/hooks/useDeals';
import { useMailContacts } from '@/hooks/useMailContacts';
import { useProductCategoryNameMap } from '@/hooks/useProductCategories';
import { type PurchaseOrderRecord, useDeliveries, usePurchaseOrders } from '@/hooks/useSalesOps';
import { SendTransferRequestsButton } from '@/pages/transfer-requests/SendTransferRequestsButton';

interface StageColumn {
  id: DealStage;
  label: string;
}

const STAGE_COLUMNS: StageColumn[] = [
  { id: 'document_requested', label: '資料請求' },
  { id: 'briefing', label: '個別説明' },
  { id: 'quote', label: '見積' },
  { id: 'won', label: '成約' },
  { id: 'agreed', label: '同意' },
  { id: 'paid', label: '入金' },
  { id: 'ordered', label: '発注' },
  { id: 'delivered', label: '発送・納品' },
  { id: 'lost', label: '失注' },
];

const STAGE_DOT_COLOR: Record<DealStage, string> = {
  document_requested: 'var(--text-muted)',
  briefing: 'var(--accent)',
  quote: 'var(--accent-amber)',
  won: 'var(--accent-green)',
  agreed: 'var(--accent-green)',
  paid: 'var(--accent-green)',
  ordered: 'var(--accent-cyan)',
  delivered: 'var(--accent-cyan)',
  lost: 'var(--accent-red)',
};

function dateLabel(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }
  return '';
}

/** 個別説明予定日時を「M/D HH:mm」で整形。 */
function briefingDateTimeLabel(v: unknown): string {
  let d: Date | null = null;
  if (typeof v === 'string') {
    const t = new Date(v);
    if (!Number.isNaN(t.getTime())) d = t;
  } else if (v && typeof v === 'object' && 'toDate' in v) {
    try {
      d = (v as { toDate: () => Date }).toDate();
    } catch {
      d = null;
    }
  }
  if (!d) return '';
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${mi}`;
}

/**
 * 個別説明ステージのカード用ステータスバッジ。
 *  - 完了: briefing_records が 1 件以上
 *  - 確定: scheduledBriefingAt が設定済 (記録未保存)
 *  - 申込/日程未定: 上記いずれでもない
 */
function BriefingStatusBadge({ deal }: { deal: DealRecord }) {
  const records = useBriefingRecords(deal.id);
  // 下書き記録は完了扱いにしない (2026-07-24)
  const completed = (records.data ?? []).some((r) => (r.status ?? 'completed') !== 'draft');
  const scheduledAt = deal.scheduledBriefingAt;

  if (completed) {
    return (
      <Badge variant="approved">
        <CheckCircle2 className="size-2.5" />
        完了
      </Badge>
    );
  }
  if (scheduledAt) {
    const label = briefingDateTimeLabel(scheduledAt);
    return (
      <Badge variant="accent">
        <CalendarCheck className="size-2.5" />
        確定{label && ` ${label}`}
      </Badge>
    );
  }
  return (
    <Badge variant="default">
      <CalendarClock className="size-2.5" />
      申込/日程未定
    </Badge>
  );
}

function isStale(deal: DealRecord): boolean {
  // lastTouchedAt から7日超で「停滞」
  let ms = 0;
  const v = deal.lastTouchedAt;
  if (typeof v === 'string') ms = Date.parse(v);
  else if (v && typeof v === 'object' && 'toMillis' in v) {
    ms = (v as { toMillis: () => number }).toMillis();
  }
  if (!ms) return false;
  return Date.now() - ms > 7 * 24 * 60 * 60 * 1000;
}

/** 発注・発送・納品の列のカードに出す「いまどこで止まっているか」(2026-10-30〜)。 */
interface FulfillmentChip {
  label: string;
  tone: 'cyan' | 'purple' | 'green' | 'muted';
}

const CHIP_CLASS: Record<FulfillmentChip['tone'], string> = {
  cyan: 'bg-[var(--accent-cyan-subtle)] text-[var(--accent-cyan)]',
  purple: 'bg-[var(--accent-purple-subtle)] text-[var(--accent-purple)]',
  green: 'bg-[var(--accent-green-subtle)] text-[var(--accent-green)]',
  muted: 'bg-[var(--bg-elevated)] text-[var(--text-muted)]',
};

interface DealCardProps {
  deal: DealRecord;
  contactName: string;
  productLineName: string;
  fulfillmentChip?: FulfillmentChip | null;
}

function DealCard({ deal, contactName, productLineName, fulfillmentChip }: DealCardProps) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  });
  const isBtob = deal.contactKind === 'btob';
  const stale = isStale(deal);

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onDoubleClick={() => navigate(`/deals/${deal.id}`)}
      className="rounded-md bg-[var(--bg-surface)] border border-[var(--border)] hover:border-[var(--accent)] p-3 cursor-grab active:cursor-grabbing transition-colors"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-[10px] text-[var(--text-muted)] font-mono truncate">
          {deal.id.slice(0, 8)}
        </span>
        <Badge variant={isBtob ? 'accent' : 'review'}>
          <span className="inline-flex items-center gap-1">
            {isBtob ? <Building2 className="size-2.5" /> : <UserCircle2 className="size-2.5" />}
            {isBtob ? 'BtoB' : 'BtoC'}
          </span>
        </Badge>
      </div>
      <div className="text-[13px] font-medium text-[var(--text-primary)] truncate mb-1">
        {contactName}
      </div>
      {productLineName && (
        <div className="text-[11px] text-[var(--text-secondary)] flex items-center gap-1 mb-1">
          <Tag className="size-2.5" />
          {productLineName}
        </div>
      )}
      {/* v3.2.19: 商談作成時に有効だった商品カタログ (価格表バージョン) のバッジ */}
      {deal.productCatalogSnapshot && deal.productCatalogSnapshot.length > 0 && (
        <div
          className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 mb-1 flex-wrap"
          title={deal.productCatalogSnapshot.map((s) => `${s.brand}: ${s.label}`).join('\n')}
        >
          <Package className="size-2.5 flex-shrink-0" />
          {deal.productCatalogSnapshot.map((s) => (
            <span
              key={s.catalogId}
              className="px-1 py-0.5 rounded bg-[var(--bg-elevated)] truncate max-w-[140px]"
            >
              {s.brand} {s.label}
            </span>
          ))}
        </div>
      )}
      {deal.stage === 'briefing' && (
        <div className="mb-1">
          <BriefingStatusBadge deal={deal} />
        </div>
      )}
      {fulfillmentChip && (
        <div className="mb-1">
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium ${CHIP_CLASS[fulfillmentChip.tone]}`}
          >
            {fulfillmentChip.label}
          </span>
        </div>
      )}
      {(deal.expectedAmount || deal.finalAmount) && (
        <div className="text-[12px] tabular-nums font-medium text-[var(--text-primary)] mb-1">
          ¥{(deal.finalAmount ?? deal.expectedAmount ?? 0).toLocaleString()}
        </div>
      )}
      <div className="flex items-center justify-between text-[10px] text-[var(--text-muted)]">
        <span>{dateLabel(deal.lastTouchedAt)}</span>
        <span className="flex items-center gap-1">
          {stale && <span className="text-[var(--accent-red)] font-medium">! 停滞</span>}
          {deal.mergeCandidateCount > 0 && (
            <span className="text-[var(--accent-amber)]">✦ {deal.mergeCandidateCount}</span>
          )}
          {deal.lostReason && (
            <span className="text-[var(--accent-red)] truncate max-w-[60px]">
              {deal.lostReason}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

interface DealsColumnProps {
  column: StageColumn;
  deals: DealRecord[];
  contactNames: Map<string, string>;
  categoryNameMap: Map<string, string>;
  fulfillmentChips: Map<string, FulfillmentChip>;
}

function DealsColumn({
  column,
  deals,
  contactNames,
  categoryNameMap,
  fulfillmentChips,
}: DealsColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div className="flex flex-col min-w-[240px] flex-1">
      <div className="flex items-center justify-between px-2 mb-2">
        <div className="flex items-center gap-2">
          <span
            className="size-1.5 rounded-full"
            style={{ background: STAGE_DOT_COLOR[column.id] }}
          />
          <span className="text-[12px] font-medium text-[var(--text-primary)]">{column.label}</span>
        </div>
        <span className="text-[11px] text-[var(--text-muted)] tabular-nums">{deals.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-2 min-h-[200px] rounded-md p-1.5 transition-colors ${
          isOver ? 'bg-[var(--accent-subtle)]' : 'bg-[var(--bg-elevated)]'
        }`}
      >
        {deals.map((d) => {
          // categoryId が真のソース、無ければ表示用スナップショット primaryCategory
          const categoryName = d.primaryCategoryId
            ? (categoryNameMap.get(d.primaryCategoryId) ?? d.primaryCategory ?? '')
            : (d.primaryCategory ?? '');
          return (
            <DealCard
              key={d.id}
              deal={d}
              contactName={contactNames.get(d.contactId) ?? '(顧客不明)'}
              productLineName={categoryName}
              fulfillmentChip={fulfillmentChips.get(d.id) ?? null}
            />
          );
        })}
        {deals.length === 0 && (
          <div className="text-[11px] text-[var(--text-muted)] text-center py-6">—</div>
        )}
      </div>
    </div>
  );
}

/**
 * ボードのフィルタ条件。localStorage に保存してリロード / 再ログイン後もキープする。
 *
 * mine (自分の担当 ON/OFF) は担当者ドロップダウンと独立したトグル。ON のとき
 * assigneeUid または salesAgentUid が自分の商談のみ表示し、担当者ドロップダウンは
 * 無効化する (v3.2.18 の外部SA と同じ「自分の担当案件」定義)。
 *
 * 旧実装は legacy role='editor' のとき「自分」を強制初期セットしていたが、
 * v2 権限では leader / employee がどちらも role='editor' に導出されるため、
 * 担当者未割当の商談が大半の運用では非 owner 全員がボード 0 件表示になっていた。
 * 現在は全ロール「全員」既定 + 任意トグルに変更。
 */
interface BoardFilters {
  mine: boolean;
  assignee: string;
  category: string;
  kind: string;
}

const DEFAULT_FILTERS: BoardFilters = {
  mine: false,
  assignee: '__all__',
  category: '__all__',
  kind: '__all__',
};

const FILTERS_STORAGE_KEY = 'dealsBoard.filters';
/** 旧実装 (担当者のみ sessionStorage 保存) からの引き継ぎ用キー。 */
const LEGACY_ASSIGNEE_KEY = 'dealsBoard.filterAssignee';

function loadInitialFilters(): BoardFilters {
  if (typeof window === 'undefined') return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<BoardFilters>;
      return {
        mine: Boolean(parsed.mine),
        assignee: typeof parsed.assignee === 'string' ? parsed.assignee : DEFAULT_FILTERS.assignee,
        category: typeof parsed.category === 'string' ? parsed.category : DEFAULT_FILTERS.category,
        kind: typeof parsed.kind === 'string' ? parsed.kind : DEFAULT_FILTERS.kind,
      };
    }
  } catch {
    // 壊れた保存値は既定値で継続
  }
  const legacy = window.sessionStorage.getItem(LEGACY_ASSIGNEE_KEY);
  if (legacy != null) return { ...DEFAULT_FILTERS, assignee: legacy };
  return DEFAULT_FILTERS;
}

export function DealsBoardPage() {
  const { user } = useAuth();
  const { data: deals, isLoading } = useDeals();
  // 顧客名解決用に全連絡先を取得する。limit を付けると updatedAt 降順の上位 N 件しか
  // Map に乗らず、枠外の連絡先を参照する商談が「(顧客不明)」になる (内部は差分同期
  // キャッシュで全件ロード済みなので limit を外しても追加 read は発生しない)。
  const { data: contacts } = useMailContacts();
  const transitionMutation = useTransitionDealStage();
  const categoryNameMap = useProductCategoryNameMap();
  const { data: purchaseOrders } = usePurchaseOrders();
  const { data: deliveries } = useDeliveries();
  // v3.2.21: 「カテゴリー管理」タブは営業設定へ移動、代わりに「保留トレイ」タブを追加
  const [tab, setTab] = useState<'board' | 'inbox'>('board');

  const [filters, setFilters] = useState<BoardFilters>(loadInitialFilters);
  const updateFilters = (patch: Partial<BoardFilters>) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  };
  const [wonLostDialog, setWonLostDialog] = useState<{
    open: boolean;
    mode: 'won' | 'lost';
    dealId: string | null;
    initialAmount?: number | null;
    contactName?: string | null;
  }>({ open: false, mode: 'won', dealId: null });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const contactNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contacts ?? []) {
      m.set(c.id, c.name || c.defaultEmail || c.id);
    }
    return m;
  }, [contacts]);

  // フィルタ選択肢: マスタ (product_categories) を優先、レガシーで categoryId が無い
  // Deal の primaryCategory (文字列) も補助的に拾う
  // 発注の列: 入荷待ち / 検品待ち · シリアル x/y / 発送待ち、発送・納品の列: 配送中 / 納品完了
  const fulfillmentChips = useMemo(() => {
    const m = new Map<string, FulfillmentChip>();
    const dealById = new Map((deals ?? []).map((d) => [d.id, d]));
    const rank = { ordered: 0, received: 1, inspected: 2 } as const;
    const behind = new Map<string, PurchaseOrderRecord>();
    for (const po of purchaseOrders ?? []) {
      if (po.status !== 'ordered' && po.status !== 'received' && po.status !== 'inspected')
        continue;
      if (dealById.get(po.dealId)?.stage !== 'ordered') continue;
      const cur = behind.get(po.dealId);
      if (!cur || rank[po.status] < rank[cur.status as keyof typeof rank]) {
        behind.set(po.dealId, po);
      }
    }
    for (const [dealId, po] of behind) {
      const deal = dealById.get(dealId);
      const isTimeWaver =
        !!deal?.primaryCategoryId && categoryNameMap.get(deal.primaryCategoryId) === 'TimeWaver';
      if (po.status === 'ordered') m.set(dealId, { label: '入荷待ち', tone: 'cyan' });
      else if (po.status === 'received') {
        const pr = inspectionProgress(po, isTimeWaver);
        m.set(dealId, {
          label: `検品待ち · ${isTimeWaver ? '機器' : 'シリアル'} ${isTimeWaver ? pr.completeUnits : pr.filledSerials}/${pr.totalUnits}`,
          tone: 'purple',
        });
      } else m.set(dealId, { label: '発送待ち', tone: 'green' });
    }
    const dlByDeal = new Map<string, { open: number; done: number }>();
    for (const dl of deliveries ?? []) {
      if (dl.status === 'cancelled') continue;
      if (dealById.get(dl.dealId)?.stage !== 'delivered') continue;
      const c = dlByDeal.get(dl.dealId) ?? { open: 0, done: 0 };
      if (dl.status === 'completed') c.done++;
      else c.open++;
      dlByDeal.set(dl.dealId, c);
    }
    for (const [dealId, c] of dlByDeal) {
      m.set(
        dealId,
        c.open > 0 ? { label: '配送中', tone: 'cyan' } : { label: '納品完了', tone: 'muted' },
      );
    }
    return m;
  }, [deals, purchaseOrders, deliveries, categoryNameMap]);

  const categoryOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    // マスタ登録分 (categoryId をフィルタ値に使う)
    for (const [id, name] of categoryNameMap.entries()) {
      opts.push({ value: `id:${id}`, label: name });
    }
    // categoryId 未付与の Deal の primaryCategory 文字列
    const legacyNames = new Set<string>();
    for (const d of deals ?? []) {
      if (!d.primaryCategoryId && d.primaryCategory) {
        legacyNames.add(d.primaryCategory);
      }
    }
    for (const n of legacyNames) {
      opts.push({ value: `name:${n}`, label: `${n} (未登録)` });
    }
    return opts;
  }, [deals, categoryNameMap]);

  // v3.2.18: 外部営業ロール (level='external') は自分の担当案件のみ閲覧可能。
  // assigneeUid === uid または salesAgentUid === uid を満たす商談だけ通す。
  // owner / leader / employee は全件閲覧可（既存挙動）。
  const isExternalSa = user?.level === 'external';

  const filtered = useMemo(() => {
    const all = deals ?? [];
    const uid = user?.uid;
    return all.filter((d) => {
      if (isExternalSa || filters.mine) {
        if (!uid) return false;
        if (d.assigneeUid !== uid && d.salesAgentUid !== uid) return false;
      }
      // 「自分の担当」ON 中は担当者ドロップダウン (UI も無効化) を適用しない
      if (!filters.mine && filters.assignee !== '__all__') {
        if (filters.assignee === '__none__') {
          if (d.assigneeUid) return false;
        } else if (d.assigneeUid !== filters.assignee) {
          return false;
        }
      }
      if (filters.category !== '__all__') {
        if (filters.category === '__none__') {
          if (d.primaryCategoryId || d.primaryCategory) return false;
        } else if (filters.category.startsWith('id:')) {
          const id = filters.category.slice(3);
          if (d.primaryCategoryId !== id) return false;
        } else if (filters.category.startsWith('name:')) {
          const name = filters.category.slice(5);
          if (d.primaryCategoryId || d.primaryCategory !== name) return false;
        }
      }
      if (filters.kind !== '__all__' && d.contactKind !== filters.kind) return false;
      return true;
    });
  }, [deals, filters, isExternalSa, user?.uid]);

  const dealsByStage = useMemo(() => {
    const map = new Map<DealStage, DealRecord[]>();
    for (const c of STAGE_COLUMNS) map.set(c.id, []);
    for (const d of filtered) {
      const list = map.get(d.stage as DealStage);
      if (list) list.push(d);
    }
    return map;
  }, [filtered]);

  const assigneeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const d of deals ?? []) {
      if (d.assigneeUid) set.add(d.assigneeUid);
    }
    return Array.from(set);
  }, [deals]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const dealId = String(active.id);
    const toStage = String(over.id) as DealStage;
    const deal = deals?.find((d) => d.id === dealId);
    if (!deal) return;
    if (deal.stage === toStage) return;

    const contactName = contactNames.get(deal.contactId) ?? null;

    if (toStage === 'won') {
      setWonLostDialog({
        open: true,
        mode: 'won',
        dealId,
        initialAmount: deal.expectedAmount,
        contactName,
      });
      return;
    }
    if (toStage === 'lost') {
      setWonLostDialog({ open: true, mode: 'lost', dealId, contactName });
      return;
    }

    transitionMutation
      .mutateAsync({ dealId, toStage })
      .then(() => {
        toast.success(
          `Stage を「${
            STAGE_COLUMNS.find((c) => c.id === toStage)?.label ?? toStage
          }」に移動しました`,
        );
      })
      .catch((err) => {
        toast.error(`Stage 変更失敗: ${err instanceof Error ? err.message : String(err)}`);
      });
  };

  return (
    <Screen
      title="商談ボード"
      description="Stage 進行を DnD で管理。Won/Lost 確定はダイアログで人間判断 (金額・失注理由が必須)。"
      // 2026-10-26: 送金依頼中のものを弊社経理へまとめて連絡 (メーカー宛ではない)。外部SA には出さない
      actions={isExternalSa ? undefined : <SendTransferRequestsButton />}
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'board' | 'inbox')}>
        <TabsList>
          <TabsTrigger value="board">
            <Building2 className="size-3.5 mr-1" />
            ボード
          </TabsTrigger>
          <TabsTrigger value="inbox">
            <Inbox className="size-3.5 mr-1" />
            保留トレイ
          </TabsTrigger>
        </TabsList>

        <TabsContent value="board">
          {/* フィルタバー (条件は localStorage にキープ) */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]">
              <Filter className="size-3" />
              フィルタ:
            </div>
            {/* 外部SA は常時自分の担当のみ表示のためトグル不要 */}
            {!isExternalSa && (
              <button
                type="button"
                aria-pressed={filters.mine}
                onClick={() => updateFilters({ mine: !filters.mine })}
                className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs transition-colors ${
                  filters.mine
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <UserCheck className="size-3" />
                自分の担当
              </button>
            )}
            <select
              value={filters.assignee}
              disabled={filters.mine}
              onChange={(e) => updateFilters({ assignee: e.target.value })}
              className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-2 py-1 text-xs disabled:opacity-40"
            >
              <option value="__all__">担当: 全員</option>
              <option value="__none__">未割当</option>
              {assigneeOptions.map((uid) => (
                <option key={uid} value={uid}>
                  {uid.slice(0, 8)}
                </option>
              ))}
            </select>
            <select
              value={filters.category}
              onChange={(e) => updateFilters({ category: e.target.value })}
              className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
            >
              <option value="__all__">カテゴリ: 全て</option>
              <option value="__none__">未割当</option>
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={filters.kind}
              onChange={(e) => updateFilters({ kind: e.target.value })}
              className="rounded border border-[var(--border)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
            >
              <option value="__all__">顧客: 全て</option>
              <option value="btob">BtoB</option>
              <option value="btoc">BtoC</option>
            </select>
          </div>

          {isLoading ? (
            <Spinner />
          ) : !deals || deals.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="商談がまだありません"
              description="営業フォーム経由で送信されると Phase 3 から自動で Deal が作成されます。手動作成は顧客詳細から可能です。"
            />
          ) : (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <div className="flex gap-3 overflow-x-auto pb-4">
                {STAGE_COLUMNS.map((col) => (
                  <DealsColumn
                    key={col.id}
                    column={col}
                    deals={dealsByStage.get(col.id) ?? []}
                    contactNames={contactNames}
                    categoryNameMap={categoryNameMap}
                    fulfillmentChips={fulfillmentChips}
                  />
                ))}
              </div>
            </DndContext>
          )}

          <WonLostDialog
            open={wonLostDialog.open}
            mode={wonLostDialog.mode}
            dealId={wonLostDialog.dealId}
            initialAmount={wonLostDialog.initialAmount}
            contactName={wonLostDialog.contactName}
            onOpenChange={(o) => setWonLostDialog((s) => ({ ...s, open: o }))}
          />
        </TabsContent>

        <TabsContent value="inbox">
          <DealsInboxContent />
        </TabsContent>
      </Tabs>
    </Screen>
  );
}
